import { useState, useRef } from "react";
import { AppLayout } from "@/components/AppLayout";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { UploadSimple, FloppyDisk, UserCircle } from "@phosphor-icons/react";

export default function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const fileRef = useRef();
  const [name, setName] = useState(user?.name || "");
  const [position, setPosition] = useState(user?.position || "");
  const [password, setPassword] = useState("");
  const [avatarPreview, setAvatarPreview] = useState(user?.avatar_url ? fileUrl(user.avatar_url) : null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const onPick = (e) => {
    const f = e.target.files[0];
    if (f) {
      setAvatarFile(f);
      setAvatarPreview(URL.createObjectURL(f));
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      let avatar_url;
      if (avatarFile) {
        const fd = new FormData();
        fd.append("file", avatarFile);
        const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        avatar_url = up.data.path;
      }
      const body = { name, position };
      if (password) body.password = password;
      if (avatar_url) body.avatar_url = avatar_url;
      await api.put("/profile/me", body);
      await refreshUser();
      toast.success("Profil berhasil diperbarui");
      setPassword("");
      setAvatarFile(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppLayout title="Profil Saya">
      <div className="max-w-2xl space-y-6 animate-fade-up">
        <Card className="p-6">
          <div className="flex items-center gap-5">
            {avatarPreview ? (
              <img src={avatarPreview} alt="avatar" className="h-20 w-20 rounded-full object-cover border border-border" data-testid="profile-avatar-preview" />
            ) : (
              <div className="h-20 w-20 rounded-full bg-secondary flex items-center justify-center">
                <UserCircle size={44} weight="duotone" className="text-muted-foreground" />
              </div>
            )}
            <div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPick} data-testid="profile-avatar-input" />
              <Button variant="outline" className="gap-2" onClick={() => fileRef.current?.click()} data-testid="profile-avatar-button">
                <UploadSimple size={18} /> Ubah Foto
              </Button>
              <p className="text-xs text-muted-foreground mt-2">JPG atau PNG, maks. beberapa MB.</p>
            </div>
          </div>
        </Card>

        <Card className="p-6 space-y-4">
          <div className="space-y-1.5">
            <Label>Nama Lengkap</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Jabatan</Label>
            <Input value={position} onChange={(e) => setPosition(e.target.value)} data-testid="profile-position-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input value={user?.email || ""} disabled />
            <p className="text-xs text-muted-foreground">Email tidak dapat diubah. Hubungi admin bila perlu.</p>
          </div>
          <div className="space-y-1.5">
            <Label>Password Baru</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Kosongkan jika tidak diubah" data-testid="profile-password-input" />
          </div>
          <Button className="gap-2" onClick={save} disabled={saving} data-testid="save-profile-button">
            <FloppyDisk size={18} /> {saving ? "Menyimpan…" : "Simpan Perubahan"}
          </Button>
        </Card>
      </div>
    </AppLayout>
  );
}
